/** Stable broadcast audio track. Inputs never connect to local speakers. Caller owns input tracks. */
export class PresenterAudio {
  readonly context = new AudioContext({ sampleRate: 48000 });
  private destination = this.context.createMediaStreamDestination();
  readonly stream = this.destination.stream;
  private micGain = this.context.createGain();
  private screenGain = this.context.createGain();
  private meter = this.context.createAnalyser();
  private limiter = this.context.createDynamicsCompressor();
  private clock = this.context.createOscillator();
  private silence = this.context.createGain();
  private mic: MediaStreamAudioSourceNode | null = null;
  private screen: MediaStreamAudioSourceNode | null = null;
  private samples = new Float32Array(1024);
  private closed = false;

  constructor() {
    this.meter.fftSize = 1024;
    this.micGain.connect(this.meter); this.meter.connect(this.limiter);
    this.screenGain.gain.value = .75; this.screenGain.connect(this.limiter);
    this.limiter.connect(this.destination);
    this.silence.gain.value = 0; this.clock.connect(this.silence); this.silence.connect(this.destination); this.clock.start();
  }
  async resume() { if (!this.closed) await this.context.resume(); }
  setMicrophone(stream: MediaStream | null) {
    const tracks = stream?.getAudioTracks().filter(t => t.readyState === 'live') ?? [];
    const next = tracks.length ? this.context.createMediaStreamSource(new MediaStream(tracks)) : null;
    this.mic?.disconnect(); this.mic = next; next?.connect(this.micGain);
  }
  setScreen(stream: MediaStream | null) {
    const tracks = stream?.getAudioTracks().filter(t => t.readyState === 'live') ?? [];
    const next = tracks.length ? this.context.createMediaStreamSource(new MediaStream(tracks)) : null;
    this.screen?.disconnect(); this.screen = next; next?.connect(this.screenGain);
  }
  setMicrophoneEnabled(enabled: boolean) { this.micGain.gain.value = enabled ? 1 : 0; }
  setScreenVolume(volume: number) { this.screenGain.gain.value = Math.max(0, Math.min(1, volume)); }
  microphoneLevel() {
    if (this.closed || this.context.state !== 'running') return 0;
    this.meter.getFloatTimeDomainData(this.samples);
    const rms = Math.sqrt(this.samples.reduce((sum, x) => sum + x * x, 0) / this.samples.length);
    return Math.round(Math.min(100, rms * 300));
  }
  close() {
    if (this.closed) return;
    this.closed = true; this.mic?.disconnect(); this.screen?.disconnect();
    this.clock.stop(); this.stream.getTracks().forEach(t => t.stop()); void this.context.close().catch(() => {});
  }
}
